import * as z from 'zod';
import { CurrencyIso } from '@spa/lib/converters/convertCurrencyIso';
import type { PageType } from './getPageType';

const definePageTypes = <T extends readonly PageType[]>(
  pageTypes: T & (Exclude<PageType, T[number]> extends never ? unknown : never)
): T => pageTypes;

const PAGE_VIEW_PAGE_TYPES = definePageTypes([
  'home',
  'produktdetail',
  'warenkorb',
  'checkout',
  'danke',
  'suche',
  'marken',
  'kategorie',
  'smart-kategorie',
  'login',
  'kundenkonto',
  'wunschliste',
  'prospekte',
  'storefinder',
  'content',
  'service',
  'not-defined'
] as const);

export const pageViewPageTypeSchema = z.enum(PAGE_VIEW_PAGE_TYPES);

export const userLoginStatusSchema = z.enum(['logged_in', 'guest']);
export type UserLoginStatus = z.infer<typeof userLoginStatusSchema>;

export const userPropertiesSchema = z.object({
  user_login_status: userLoginStatusSchema
});
export type PageViewUserProperties = z.infer<typeof userPropertiesSchema>;

const pageCurrencySchema = z.union([
  z.literal(CurrencyIso.EUR),
  z.literal(CurrencyIso.USD),
  z.literal(CurrencyIso.GBP),
  z.literal(CurrencyIso.CHF),
  z.literal(CurrencyIso.HUF),
  z.literal(CurrencyIso.JPY)
]);

const commonPageViewSchema = z.object({
  page_type: pageViewPageTypeSchema,
  page_language: z.string().regex(/^[a-z]{2}$/),
  page_country: z.string().regex(/^[A-Z]{2}$/),
  page_currency: pageCurrencySchema,
  page_location: z.url(),
  page_referrer: z.string(),
  user_properties: userPropertiesSchema,
  anonymousId: z.string().nonempty(),
  authenticatedId: z.string().nullable()
});

export const customPageViewEventSchema = commonPageViewSchema.extend({
  event: z.literal('custom_page_view')
});
export type CustomPageViewEvent = z.infer<typeof customPageViewEventSchema>;

export const virtualPageViewEventSchema = commonPageViewSchema.extend({
  event: z.literal('virtual_page_view')
});
export type VirtualPageViewEvent = z.infer<typeof virtualPageViewEventSchema>;

export const pageViewTrackingEventSchema = z.discriminatedUnion('event', [
  customPageViewEventSchema,
  virtualPageViewEventSchema
]);
export type PageViewTrackingEvent = z.infer<typeof pageViewTrackingEventSchema>;
